/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ConsultantInvoiceStatus, type PaymentMode } from '@therapy/shared-validators';

import { apiClient } from './client.js';

export interface ConsultantInvoiceLine {
  readonly date: string;
  readonly clientName: string;
  readonly status: string;
  readonly payPct: number;
  readonly amountPaise: number;
}

export interface ConsultantInvoice {
  readonly id: string;
  readonly therapistId: string;
  readonly therapistName: string;
  readonly invoiceNumber: string;
  readonly status: ConsultantInvoiceStatus;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly sessionsCount: number;
  readonly subtotalPaise: number;
  readonly gstPaise: number;
  readonly totalPaise: number;
  readonly lineItems: readonly ConsultantInvoiceLine[];
  readonly remarks: string | null;
  readonly adminNote: string | null;
  readonly paymentMode: string | null;
  readonly submittedAt: string | null;
  readonly approvedAt: string | null;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

const BASE = '/consultant-invoices';

export const consultantInvoicesApi = {
  async mine(): Promise<readonly ConsultantInvoice[]> {
    const { data } = await apiClient.get<readonly ConsultantInvoice[]>(`${BASE}/mine`);
    return data;
  },
  async list(status?: ConsultantInvoiceStatus): Promise<readonly ConsultantInvoice[]> {
    const { data } = await apiClient.get<readonly ConsultantInvoice[]>(BASE, {
      params: status !== undefined ? { status } : {},
    });
    return data;
  },
  async generate(month: string): Promise<ConsultantInvoice> {
    const { data } = await apiClient.post<ConsultantInvoice>(`${BASE}/generate`, { month });
    return data;
  },
  async submit(id: string, remarks?: string): Promise<ConsultantInvoice> {
    const { data } = await apiClient.post<ConsultantInvoice>(`${BASE}/${id}/submit`, { remarks });
    return data;
  },
  async approve(id: string): Promise<ConsultantInvoice> {
    const { data } = await apiClient.patch<ConsultantInvoice>(`${BASE}/${id}/approve`);
    return data;
  },
  async reject(id: string, note: string): Promise<ConsultantInvoice> {
    const { data } = await apiClient.patch<ConsultantInvoice>(`${BASE}/${id}/reject`, { note });
    return data;
  },
  async pay(id: string, paymentMode: PaymentMode): Promise<ConsultantInvoice> {
    const { data } = await apiClient.patch<ConsultantInvoice>(`${BASE}/${id}/pay`, { paymentMode });
    return data;
  },
};
